import { authService } from '@/services/auth.service';
import { TokenPair, User } from '@/types/auth.types';
import { useOrganisationsStore } from '@/store/organisations.store';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  user: User | null;
  tokens: TokenPair | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthActions {
  setUser: (user: User | null) => void;
  setTokens: (tokens: TokenPair | null) => void;
  setError: (error: string | null) => void;
  setLoading: (isLoading: boolean) => void;
  logout: () => Promise<void>;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    set => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setUser: user => set({ user, isAuthenticated: !!user }),
      setTokens: tokens => set({ tokens }),
      setError: error => set({ error }),
      setLoading: isLoading => set({ isLoading }),
      logout: async () => {
        try {
          // Call the logout API
          await authService.logout();
        } catch (error) {
          console.error('Logout error:', error);
          // Continue with local cleanup even if API call fails
        } finally {
          // Always clear local state
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            error: null,
          });
          // Nettoyer l'organisation sélectionnée (et la clé persistée) pour ne
          // pas envoyer un `x-organisation-uuid` périmé après changement de
          // compte sur le même navigateur.
          try {
            useOrganisationsStore.getState().reset();
          } catch {
            // ignore
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
